import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Tabs,
    Tab,
    Paper,
    TextField,
    Button,
    Divider,
    Container,
    Link,
    Chip,
    IconButton,
    Menu,
    MenuItem,
} from '@mui/material';
import { MoreHoriz, Add, CreditCard, AccessTime } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useVendorStore } from '../../../store/vendorStore';
import { requestVendorPasswordResetEmail } from '../../../services/auth.service';
import { toast } from 'react-toastify';
import { useLoadingDialogStore } from '../../../store/loadingDialogStore';
import { useLoginFormDialogStore } from '../../../store/loginFormDialogStore';
import { useOpenConfirmDialog } from '../../../store/confirmDialogStore';
import { useNicepayScript } from '../../../hooks/useNicepayScript';
import { openNicepayAuth, NicepayParams } from '../../../utils/nicepayAuth';
import { post, get, del, patch } from '../../../helpers/api.helper';
import config from '../../../config/config';
import SubscriptionCancelDialog from '../../../components/Dialog/SubscriptionCancelDialog';
import CreditPurchaseDialog from '../../../components/Dialog/CreditPurchaseDialog';
import ConfirmDialog from '../../../components/Dialog/ConfirmDialog';
import {
    getUserSubscription,
    UserSubscription,
    UserCard,
    OrderItem,
    CreditProduct,
    cancelOrder,
    refundOrder,
} from '../../../services/billing.service';
import { TopNavigation } from '../Home/MainPage2';
import Footer from '../Home/Footer';
import HomeContainer from '../Home/HomeContainer';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

interface BillingRegisterResponse {
    MID: string;
    GoodsName: string;
    Amt: number;
    Moid: string;
    EdiDate: string;
    SignData: string;
    ReturnURL: string;
    BillAuthYN: string;
    PayMethod: string;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`account-tabpanel-${index}`}
            aria-labelledby={`account-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
        </div>
    );
}

export default function AccountSettingsPage() {
    const [tabValue, setTabValue] = useState(0);
    const [userEmail, setUserEmail] = useState<string>('');
    const [loading, setLoading] = useState(true);
    const [subscriptionLoading, setSubscriptionLoading] = useState(false);
    const [subscriptionData, setSubscriptionData] = useState<UserSubscription | null>(null);
    const [cardLoading, setCardLoading] = useState(false);
    const [cardData, setCardData] = useState<UserCard[]>([]);
    const [orderLoading, setOrderLoading] = useState(false);
    const [orderData, setOrderData] = useState<OrderItem[]>([]);
    const [creditLoading, setCreditLoading] = useState(false);
    const [creditData, setCreditData] = useState<{ subFittingAmount: number; genFittingAmount: number } | null>(null);
    const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedCardId, setSelectedCardId] = useState<string | null>(null);
    const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
    const [creditPurchaseDialogOpen, setCreditPurchaseDialogOpen] = useState(false);
    const [cancelingOrderId, setCancelingOrderId] = useState<string | null>(null);
    const { vendorInfo, vendorId } = useVendorStore();
    const { setIsLoadingOpen } = useLoadingDialogStore((state) => state.action);
    const { setIsOpen: openLoginDialog } = useLoginFormDialogStore((state) => state.action);
    const openConfirmDialog = useOpenConfirmDialog();
    const navigate = useNavigate();
    const location = useLocation();

    // NICEPAY 스크립트 로딩 상태
    const { ready: scriptReady, error: scriptError } = useNicepayScript(
        'https://pg-web.nicepay.co.kr/v3/common/js/nicepay-pgweb.js',
    );

    useEffect(() => {
        const fetchUserInfo = async () => {
            // 벤더 로그인 상태인 경우 벤더 정보 사용
            if (vendorInfo && vendorId) {
                try {
                    setUserEmail(vendorInfo.email || '');
                } catch (error) {
                    console.error('벤더 정보를 가져오는데 실패했습니다:', error);
                } finally {
                    setLoading(false);
                }
            } else {
                setLoading(false);
            }
        };

        fetchUserInfo();
    }, [vendorInfo, vendorId]);

    // URL 해시를 확인하여 구독 및 결제 탭으로 이동
    useEffect(() => {
        if (location.hash === '#billing') {
            setTabValue(1);
        }
    }, [location.hash]);

    // 구독 정보 가져오기
    const fetchSubscriptionData = async () => {
        if (!vendorId) return;

        try {
            setSubscriptionLoading(true);
            const response = await getUserSubscription(vendorId);
            setSubscriptionData(response.item);
        } catch (error) {
            console.error('구독 정보를 가져오는데 실패했습니다:', error);
            toast.error('구독 정보를 불러오는데 실패했습니다.');
        } finally {
            setSubscriptionLoading(false);
        }
    };

    // 카드 정보 가져오기
    const fetchCardData = async () => {
        if (!vendorId) return;

        try {
            setCardLoading(true);
            // parseServiceResponse를 우회하고 직접 API 호출
            const response = await get(`${config.api.matching_api}/billing/user/${vendorId}/card`);

            // API 응답에서 itemList 추출
            const cardList = response.data?.itemList || [];

            // isUsed='t'인 카드를 맨 위로 정렬
            const sortedCardList = cardList.sort((a: any, b: any) => {
                if (a.isUsed === 't' && b.isUsed !== 't') return -1;
                if (a.isUsed !== 't' && b.isUsed === 't') return 1;
                return 0;
            });

            setCardData(sortedCardList as UserCard[]);
        } catch (error) {
            console.error('카드 정보를 가져오는데 실패했습니다:', error);
            toast.error('카드 정보를 불러오는데 실패했습니다.');
        } finally {
            setCardLoading(false);
        }
    };

    // 주문 내역 가져오기
    const fetchOrderData = async () => {
        if (!vendorId) return;

        try {
            setOrderLoading(true);
            // parseServiceResponse를 우회하고 직접 API 호출
            const response = await get(`${config.api.matching_api}/billing/user/${vendorId}/order`);

            // API 응답에서 itemList 추출
            const orderList = response.data?.itemList || [];

            setOrderData(orderList as OrderItem[]);
        } catch (error) {
            console.error('주문 내역을 가져오는데 실패했습니다:', error);
            toast.error('주문 내역을 불러오는데 실패했습니다.');
        } finally {
            setOrderLoading(false);
        }
    };

    // 크레딧 정보 가져오기
    const fetchCreditData = async () => {
        if (!vendorId) return;

        try {
            setCreditLoading(true);
            const response = await get(`${config.api.matching_api}/vendor/${vendorId}/fitting-credit?limit=1`);
            // API 응답에서 첫 번째 데이터 추출 (중첩된 data 구조)
            const creditInfo = response.data?.data?.[0];

            if (creditInfo) {
                const creditData = {
                    subFittingAmount: creditInfo.subFittingAmount || 0,
                    genFittingAmount: creditInfo.genFittingAmount || 0,
                };
                setCreditData(creditData);
            } else {
                setCreditData(null);
            }
        } catch (error) {
            console.error('크레딧 정보를 가져오는데 실패했습니다:', error);
            toast.error('크레딧 정보를 불러오는데 실패했습니다.');
        } finally {
            setCreditLoading(false);
        }
    };

    // 구독 및 결제 탭이 활성화될 때 구독 정보, 카드 정보, 주문 내역, 크레딧 정보 가져오기
    useEffect(() => {
        if (tabValue === 1) {
            if (vendorId) {
                fetchSubscriptionData();
                fetchCardData();
                fetchOrderData();
                fetchCreditData();
            }
        }
    }, [tabValue, vendorId]);

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    const handleChangePassword = async () => {
        if (!userEmail) {
            toast.error('이메일 정보를 찾을 수 없습니다.');
            return;
        }

        try {
            setIsLoadingOpen(true);

            // 벤더 로그인 상태인 경우 벤더용 API 사용
            await requestVendorPasswordResetEmail(userEmail);

            toast.success('비밀번호 재설정 이메일이 발송되었습니다. 이메일을 확인해주세요.');
        } catch (error) {
            console.error('비밀번호 재설정 이메일 발송 실패:', error);
            toast.error('이메일 발송에 실패했습니다. 다시 시도해주세요.');
        } finally {
            setIsLoadingOpen(false);
        }
    };

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, cardId: string) => {
        setMenuAnchorEl(event.currentTarget);
        setSelectedCardId(cardId);
    };

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
        setSelectedCardId(null);
    };

    const handleRefundSubscription = () => {
        setCancelDialogOpen(true);
    };

    const handleConfirmRefund = async () => {
        if (!subscriptionData?.id || !vendorId) {
            toast.error('구독 정보를 찾을 수 없습니다.');
            return;
        }

        try {
            setIsLoadingOpen(true);
            // 구독 취소 API 호출
            await patch(
                `${config.api.matching_api}/billing/user/${vendorId}/subscription/${subscriptionData.id}/cancel`,
            );
            toast.success('구독이 취소되었습니다.');
            // 구독 정보 새로고침
            await fetchSubscriptionData();
        } catch (error) {
            console.error('구독 취소 실패:', error);
            toast.error('구독 취소에 실패했습니다. 다시 시도해주세요.');
        } finally {
            setIsLoadingOpen(false);
        }
    };

    const handleRenewSubscription = async () => {
        if (!subscriptionData?.id || !vendorId) {
            toast.error('구독 정보를 찾을 수 없습니다.');
            return;
        }

        try {
            setIsLoadingOpen(true);
            // 구독 취소 철회 API 호출
            await patch(
                `${config.api.matching_api}/billing/user/${vendorId}/subscription/${subscriptionData.id}/renew`,
            );
            toast.success('구독 취소 철회 되었습니다.');
            // 구독 정보 새로고침
            await fetchSubscriptionData();
        } catch (error) {
            console.error('구독 취소 철회 실패:', error);
            toast.error('구독 취소 철회에 실패했습니다. 다시 시도해주세요.');
        } finally {
            setIsLoadingOpen(false);
        }
    };

    const handleDeleteCard = async () => {
        if (!selectedCardId) return;

        try {
            // 카드 삭제 API 호출
            await del(`${config.api.matching_api}/billing/card/${selectedCardId}`);
            toast.success('카드가 삭제되었습니다.');
            // 카드 목록 새로고침
            if (vendorId) {
                await fetchCardData();
            }
        } catch (error) {
            console.error('카드 삭제 실패:', error);
            toast.error('카드 삭제에 실패했습니다.');
        } finally {
            handleMenuClose();
        }
    };

    const handleSetDefaultCard = async () => {
        if (!selectedCardId) return;

        try {
            // TODO: 기본카드 설정 API 호출
            // await setDefaultCard(selectedCardId);
            toast.success('기본카드로 설정되었습니다.');
            // 카드 목록 새로고침
            if (vendorId) {
                await fetchCardData();
            }
        } catch (error) {
            console.error('기본카드 설정 실패:', error);
            toast.error('기본카드 설정에 실패했습니다.');
        } finally {
            handleMenuClose();
        }
    };

    const handleCardRegistrationSuccess = (data: any) => {
        // 카드 등록 성공 처리
        toast.success('카드가 성공적으로 등록되었습니다.');
        // 카드 목록 새로고침
        if (vendorId) {
            fetchCardData();
        }
    };

    const handleCardRegistrationError = (error: Error) => {
        // 카드 등록 실패 처리
        console.error('Card registration failed:', error);
        toast.error('카드 등록에 실패했습니다. 다시 시도해주세요.');
    };

    const handleCreditPurchase = async (product: CreditProduct) => {
        // 크레딧 구매 완료 후 관련 데이터 새로고침
        try {
            // 크레딧 정보 새로고침
            await fetchCreditData();
            // 청구서 내역 새로고침
            await fetchOrderData();
            // 구독 정보도 새로고침 (크레딧 구매로 인한 구독 상태 변경 가능성)
            await fetchSubscriptionData();
        } catch (error) {
            console.error('구매 완료 후 데이터 새로고침 실패:', error);
        }
    };

    // 14일 이내 주문인지 확인하는 함수
    const isOrderWithin14Days = (orderDate: string): boolean => {
        const orderDateTime = new Date(orderDate);
        const currentDate = new Date();
        const diffTime = currentDate.getTime() - orderDateTime.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays <= 14;
    };

    // 취소 가능한 주문인지 확인하는 함수
    const isCancellable = (order: OrderItem): boolean => {
        return (
            order.orderStatusCode === 20000 && // 결제완료 상태
            isOrderWithin14Days(order.createdAt) // 14일 이내
        );
    };

    // 주문 취소 처리 함수
    const handleCancelOrder = async (order: OrderItem) => {
        if (!vendorId) {
            toast.error('사용자 정보를 찾을 수 없습니다.');
            return;
        }

        openConfirmDialog('주문 취소', `${order.productName} 주문을 취소하시겠습니까?`, async () => {
            try {
                setCancelingOrderId(order.id);
                await refundOrder(vendorId, order.id);
                toast.success('주문이 취소되었습니다.');
                // 청구서 내역 새로고침
                await fetchOrderData();
                // 크레딧 정보도 새로고침
                await fetchCreditData();
                // 구독 정보도 새로고침 (환불 처리로 인한 구독 상태 변경 가능성)
                await fetchSubscriptionData();
            } catch (error) {
                console.error('주문 취소 실패:', error);
                toast.error('주문 취소에 실패했습니다. 다시 시도해주세요.');
            } finally {
                setCancelingOrderId(null);
            }
        });
    };

    const handleAddPaymentMethod = async () => {
        try {
            setCardLoading(true);

            // 로그인 사용자 필수 (벤더만)
            if (!vendorId) {
                toast.error('로그인이 필요합니다. 로그인 후 다시 시도해주세요.');
                openLoginDialog(true);
                return;
            }

            // 백엔드에서 카드 등록을 위한 파라미터 요청
            const response = await post(`${config.api.matching_api}/billing/register`, {
                userId: vendorId,
                goodsName: '카드 등록',
                amt: 0,
            });

            const data: BillingRegisterResponse = response.data;

            if (!data?.MID) {
                throw new Error('등록 파라미터를 받을 수 없습니다.');
            }

            // NICEPAY 인증창 오픈 (팝업으로 열기)
            await openNicepayAuth(data as unknown as NicepayParams, {
                popup: true,
                onSuccess: (result) => {
                    handleCardRegistrationSuccess(result);
                },
                onError: (error) => {
                    console.error('카드 등록 실패:', error);
                    const errorMessage = error instanceof Error ? error.message : '카드 등록에 실패했습니다.';
                    toast.error(errorMessage);
                },
            });
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : '알 수 없는 오류가 발생했습니다.';
            toast.error(errorMessage);
        } finally {
            setCardLoading(false);
        }
    };

    if (loading) {
        return (
            <Container maxWidth="md" sx={{ py: 4 }}>
                <Typography>로딩 중...</Typography>
            </Container>
        );
    }

    return (
        <HomeContainer>
            <TopNavigation />
            <Container maxWidth="md" sx={{ py: 9, minHeight: '70vh' }}>
                <Typography
                    variant="h4"
                    sx={{
                        fontSize: { xs: '24px', md: '40px' },
                        fontWeight: 800,
                        color: '#1F2937',
                        mb: 7,
                    }}
                >
                    계정 설정
                </Typography>

                <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
                    <Tabs
                        value={tabValue}
                        onChange={handleTabChange}
                        sx={{
                            borderBottom: 1,
                            borderColor: 'divider',
                            '& .MuiTab-root': {
                                textTransform: 'none',
                                fontWeight: 600,
                                fontSize: '16px',
                                minHeight: 48,
                                color: '#6B7280',
                                '&.Mui-selected': {
                                    color: '#374151 !important',
                                },
                            },
                            '& .MuiTabs-indicator': {
                                backgroundColor: '#374151',
                                height: 2,
                            },
                        }}
                    >
                        <Tab label="정보" />
                        <Tab label="구독 및 결제" />
                    </Tabs>

                    <TabPanel value={tabValue} index={0}>
                        <Box sx={{ px: 3, pb: 3 }}>
                            {/* Email Section */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    py: 3,
                                    borderBottom: '1px solid #E5E7EB',
                                }}
                            >
                                <Typography
                                    sx={{
                                        minWidth: 120,
                                        fontSize: '16px',
                                        color: '#6B7280',
                                        fontWeight: 500,
                                    }}
                                >
                                    이메일
                                </Typography>
                                <Typography
                                    sx={{
                                        flex: 1,
                                        fontSize: '16px',
                                        color: '#1F2937',
                                        fontWeight: 500,
                                    }}
                                >
                                    {userEmail}
                                </Typography>
                            </Box>

                            {/* Password Section */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    py: 3,
                                    flexDirection: { xs: 'column', sm: 'row' },
                                    alignItems: { xs: 'flex-start', sm: 'center' },
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        width: { xs: '100%', sm: 'auto' },
                                        flex: { xs: 'none', sm: 1 },
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            minWidth: 120,
                                            fontSize: '16px',
                                            color: '#6B7280',
                                            fontWeight: 500,
                                        }}
                                    >
                                        비밀번호
                                    </Typography>
                                    <Typography
                                        sx={{
                                            flex: 1,
                                            fontSize: '16px',
                                            color: '#1F2937',
                                            fontWeight: 500,
                                        }}
                                    >
                                        ••••••••
                                    </Typography>
                                </Box>
                                <Button
                                    variant="contained"
                                    onClick={handleChangePassword}
                                    sx={{
                                        ml: { xs: 0, sm: 2 },
                                        mt: { xs: 2, sm: 0 },
                                        backgroundColor: '#374151',
                                        color: 'white',
                                        textTransform: 'none',
                                        fontWeight: 500,
                                        px: 3,
                                        py: 0.5,
                                        borderRadius: 2,
                                        fontSize: '14px',
                                        width: { xs: '100%', sm: 'auto' },
                                        '&:hover': {
                                            backgroundColor: '#1F2937',
                                        },
                                    }}
                                >
                                    비밀번호 변경
                                </Button>
                            </Box>
                        </Box>
                    </TabPanel>

                    <TabPanel value={tabValue} index={1}>
                        <Box sx={{ px: 3, pb: 3 }}>
                            {/* CMS 결제 사용자 안내 메시지 */}
                            {subscriptionData && subscriptionData.billingType === 'cms' && (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'flex-end',
                                        mb: 2,
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            color: '#DC2626',
                                            fontSize: '12px',
                                            fontWeight: 400,
                                            backgroundColor: '#FEF2F2',
                                            border: '1px solid #FECACA',
                                            borderRadius: 1,
                                            px: 2,
                                            py: 1,
                                        }}
                                    >
                                        CMS 결제 사용자는 구독 취소 및 플랜 변경이 제한됩니다. 문의사항이 있으시면
                                        관리자에게 연락해 주세요.
                                    </Typography>
                                </Box>
                            )}

                            {/* Subscription Plan Card */}
                            {subscriptionLoading ? (
                                <Box
                                    sx={{
                                        backgroundColor: '#F9FAFB',
                                        borderRadius: 2,
                                        p: 3,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        mb: 3,
                                    }}
                                >
                                    <Typography sx={{ color: '#6B7280' }}>구독 정보를 불러오는 중...</Typography>
                                </Box>
                            ) : subscriptionData ? (
                                <Box
                                    sx={{
                                        backgroundColor: '#F9FAFB',
                                        borderRadius: 2,
                                        p: 3,
                                        display: 'flex',
                                        flexDirection: { xs: 'column', sm: 'row' },
                                        alignItems: { xs: 'flex-start', sm: 'center' },
                                        justifyContent: 'space-between',
                                        mb: 3,
                                        gap: { xs: 2, sm: 0 },
                                    }}
                                >
                                    {/* Plan Info */}
                                    <Box>
                                        {/* 취소 날짜 배지 */}
                                        {subscriptionData.canceledBillingAt && (
                                            <Box
                                                sx={{
                                                    backgroundColor: '#E5E7EB',
                                                    borderRadius: 2,
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: 0.2,
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        fontSize: '12px',
                                                        color: '#374151',
                                                        fontWeight: 500,
                                                    }}
                                                >
                                                    {new Date(subscriptionData.canceledBillingAt).toLocaleDateString(
                                                        'ko-KR',
                                                        {
                                                            month: 'numeric',
                                                            day: 'numeric',
                                                        },
                                                    )}{' '}
                                                    환불
                                                </Typography>
                                                <AccessTime
                                                    sx={{
                                                        fontSize: '12px',
                                                        color: '#374151',
                                                        mb: 0.2,
                                                    }}
                                                />
                                            </Box>
                                        )}

                                        <Typography
                                            sx={{
                                                fontSize: '16px',
                                                color: '#374151',
                                                fontWeight: 500,
                                                mb: 0.5,
                                            }}
                                        >
                                            현재 플랜:{' '}
                                            <Box component="span" sx={{ fontWeight: 700 }}>
                                                {subscriptionData.product.name}
                                            </Box>
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: '14px',
                                                color: '#6B7280',
                                            }}
                                        >
                                            {subscriptionData.status !== 'active' && subscriptionData.endDate ? (
                                                <>
                                                    구독이{' '}
                                                    {new Date(subscriptionData.endDate).toLocaleDateString('ko-KR', {
                                                        year: 'numeric',
                                                        month: 'long',
                                                        day: 'numeric',
                                                    })}
                                                    에 환불됩니다.
                                                </>
                                            ) : (
                                                <>
                                                    구독이{' '}
                                                    {new Date(subscriptionData.nextBillingAt).toLocaleDateString(
                                                        'ko-KR',
                                                        {
                                                            year: 'numeric',
                                                            month: 'long',
                                                            day: 'numeric',
                                                        },
                                                    )}
                                                    에 갱신됩니다. <br />(
                                                    {subscriptionData.product.billingCycle === 'yearly' ? '연' : '월'}{' '}
                                                    {subscriptionData.product.totalCharge.toLocaleString()}
                                                    원)
                                                </>
                                            )}
                                        </Typography>
                                    </Box>

                                    {/* Action Buttons */}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: { xs: 'column', sm: 'row' },
                                            alignItems: { xs: 'stretch', sm: 'center' },
                                            gap: { xs: 1, sm: 2 },
                                            width: { xs: '100%', sm: 'auto' },
                                        }}
                                    >
                                        {subscriptionData.status === 'paused' ? (
                                            <Button
                                                variant="outlined"
                                                onClick={handleRenewSubscription}
                                                disabled={subscriptionData.billingType === 'cms'}
                                                sx={{
                                                    backgroundColor: 'white',
                                                    color:
                                                        subscriptionData.billingType === 'cms' ? '#9CA3AF' : '#374151',
                                                    borderColor:
                                                        subscriptionData.billingType === 'cms' ? '#E5E7EB' : '#D1D5DB',
                                                    textTransform: 'none',
                                                    fontWeight: 500,
                                                    px: 2,
                                                    py: 0.5,
                                                    borderRadius: 2,
                                                    fontSize: '14px',
                                                    width: { xs: '100%', sm: 'auto' },
                                                    '&:hover': {
                                                        backgroundColor:
                                                            subscriptionData.billingType === 'cms'
                                                                ? 'white'
                                                                : '#F9FAFB',
                                                        borderColor:
                                                            subscriptionData.billingType === 'cms'
                                                                ? '#E5E7EB'
                                                                : '#9CA3AF',
                                                    },
                                                }}
                                            >
                                                구독 취소 철회
                                            </Button>
                                        ) : (
                                            <Button
                                                variant="outlined"
                                                onClick={handleRefundSubscription}
                                                disabled={subscriptionData.billingType === 'cms'}
                                                sx={{
                                                    backgroundColor: 'white',
                                                    color:
                                                        subscriptionData.billingType === 'cms' ? '#9CA3AF' : '#374151',
                                                    borderColor:
                                                        subscriptionData.billingType === 'cms' ? '#E5E7EB' : '#D1D5DB',
                                                    textTransform: 'none',
                                                    fontWeight: 500,
                                                    px: 2,
                                                    py: 0.5,
                                                    borderRadius: 2,
                                                    fontSize: '14px',
                                                    width: { xs: '100%', sm: 'auto' },
                                                    '&:hover': {
                                                        backgroundColor:
                                                            subscriptionData.billingType === 'cms'
                                                                ? 'white'
                                                                : '#F9FAFB',
                                                        borderColor:
                                                            subscriptionData.billingType === 'cms'
                                                                ? '#E5E7EB'
                                                                : '#9CA3AF',
                                                    },
                                                }}
                                            >
                                                구독 취소
                                            </Button>
                                        )}
                                        <Button
                                            variant="contained"
                                            disabled={subscriptionData.billingType === 'cms'}
                                            sx={{
                                                backgroundColor:
                                                    subscriptionData.billingType === 'cms' ? '#E5E7EB' : '#374151',
                                                color: subscriptionData.billingType === 'cms' ? '#9CA3AF' : 'white',
                                                textTransform: 'none',
                                                fontWeight: 500,
                                                px: 2,
                                                py: 0.5,
                                                borderRadius: 2,
                                                fontSize: '14px',
                                                width: { xs: '100%', sm: 'auto' },
                                                '&:hover': {
                                                    backgroundColor:
                                                        subscriptionData.billingType === 'cms' ? '#E5E7EB' : '#1F2937',
                                                },
                                            }}
                                            onClick={() => navigate('/subscription')}
                                        >
                                            플랜 변경
                                        </Button>
                                    </Box>
                                </Box>
                            ) : (
                                <Box
                                    sx={{
                                        backgroundColor: '#F9FAFB',
                                        borderRadius: 2,
                                        p: 3,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        mb: 3,
                                    }}
                                >
                                    <Typography sx={{ color: '#6B7280' }}>구독 정보가 없습니다.</Typography>
                                </Box>
                            )}

                            {/* Credit Information */}
                            {creditLoading ? (
                                <Box
                                    sx={{
                                        backgroundColor: '#F9FAFB',
                                        borderRadius: 2,
                                        p: 3,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        mb: 3,
                                    }}
                                >
                                    <Typography sx={{ color: '#6B7280' }}>크레딧 정보를 불러오는 중...</Typography>
                                </Box>
                            ) : (
                                <Box
                                    sx={{
                                        backgroundColor: '#F3F4F6',
                                        borderRadius: 2,
                                        p: 3,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        mb: 3,
                                        border: '1px solid #E5E7EB',
                                    }}
                                >
                                    <Box>
                                        <Typography
                                            sx={{
                                                fontSize: '16px',
                                                color: '#374151',
                                                fontWeight: 500,
                                                mb: 0.5,
                                            }}
                                        >
                                            현재 크레딧
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: '12px',
                                                fontWeight: 600,
                                                color: '#6B7280',
                                            }}
                                        >
                                            총{' '}
                                            {(
                                                (creditData?.subFittingAmount || 0) +
                                                (creditData?.genFittingAmount || 0)
                                            ).toLocaleString()}{' '}
                                            크레딧
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Box sx={{ textAlign: 'right' }}>
                                            <Typography
                                                sx={{
                                                    fontSize: '11px',
                                                    color: '#6B7280',
                                                    mb: 0.5,
                                                }}
                                            >
                                                구독 크레딧: {(creditData?.subFittingAmount || 0).toLocaleString()}
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    fontSize: '11px',
                                                    color: '#6B7280',
                                                }}
                                            >
                                                일반 크레딧: {(creditData?.genFittingAmount || 0).toLocaleString()}
                                            </Typography>
                                        </Box>
                                        <Button
                                            variant="contained"
                                            sx={{
                                                backgroundColor: '#374151',
                                                color: 'white',
                                                textTransform: 'none',
                                                fontWeight: 500,
                                                px: 2,
                                                py: 0.5,
                                                borderRadius: 2,
                                                fontSize: '14px',
                                                '&:hover': {
                                                    backgroundColor: '#1F2937',
                                                },
                                            }}
                                            onClick={() => {
                                                setCreditPurchaseDialogOpen(true);
                                            }}
                                        >
                                            추가구매
                                        </Button>
                                    </Box>
                                </Box>
                            )}

                            <Divider sx={{ my: 3, mb: 6 }} />
                            {/* Payment Method Section */}
                            <Box sx={{ mb: 4 }}>
                                <Typography
                                    sx={{
                                        fontSize: '14px',
                                        fontWeight: 600,
                                        color: '#1F2937',
                                        mb: 2,
                                    }}
                                >
                                    결제 방식
                                </Typography>

                                {/* Card List */}
                                <Box sx={{ mb: 2 }}>
                                    {cardLoading ? (
                                        <Box sx={{ py: 2, textAlign: 'center' }}>
                                            <Typography sx={{ color: '#6B7280' }}>
                                                카드 정보를 불러오는 중...
                                            </Typography>
                                        </Box>
                                    ) : cardData?.length > 0 ? (
                                        (() => {
                                            return cardData?.map((card, index) => {
                                                return (
                                                    <Box
                                                        key={card.id}
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between',
                                                            py: 2,
                                                        }}
                                                    >
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                            <CreditCard sx={{ fontSize: '20px', color: '#6B7280' }} />
                                                            <Typography sx={{ fontSize: '16px', color: '#374151' }}>
                                                                {card.cardName} {card.cardNo}
                                                            </Typography>
                                                            <IconButton
                                                                size="small"
                                                                sx={{ color: '#6B7280', padding: '4px' }}
                                                                onClick={(e) => handleMenuOpen(e, card.id)}
                                                            >
                                                                <MoreHoriz sx={{ fontSize: '16px' }} />
                                                            </IconButton>
                                                            {card.isUsed === 't' && (
                                                                <Chip
                                                                    label="사용중"
                                                                    size="small"
                                                                    sx={{
                                                                        backgroundColor: '#D1FAE5',
                                                                        color: '#065F46',
                                                                        fontSize: '12px',
                                                                        fontWeight: 500,
                                                                        height: '20px',
                                                                    }}
                                                                />
                                                            )}
                                                        </Box>
                                                    </Box>
                                                );
                                            });
                                        })()
                                    ) : (
                                        <Box sx={{ py: 2, textAlign: 'center' }}>
                                            <Typography sx={{ color: '#6B7280' }}>등록된 카드가 없습니다.</Typography>
                                        </Box>
                                    )}
                                </Box>

                                {/* Card Menu */}
                                <Menu
                                    anchorEl={menuAnchorEl}
                                    open={Boolean(menuAnchorEl)}
                                    onClose={handleMenuClose}
                                    anchorOrigin={{
                                        vertical: 'bottom',
                                        horizontal: 'right',
                                    }}
                                    transformOrigin={{
                                        vertical: 'top',
                                        horizontal: 'right',
                                    }}
                                >
                                    {(() => {
                                        const selectedCard = cardData.find((card) => card.id === selectedCardId);
                                        const isCardInUse = selectedCard?.isUsed === 't';
                                        const hasActiveSubscription = subscriptionData?.status === 'active';

                                        return (
                                            <>
                                                <MenuItem
                                                    onClick={handleSetDefaultCard}
                                                    sx={{
                                                        color: '#374151',
                                                        fontSize: '12px',
                                                        minWidth: '120px',
                                                        px: 2,
                                                        '&:hover': {
                                                            backgroundColor: '#F9FAFB',
                                                        },
                                                    }}
                                                >
                                                    기본카드로 설정
                                                </MenuItem>
                                                <MenuItem
                                                    onClick={handleDeleteCard}
                                                    disabled={isCardInUse && hasActiveSubscription}
                                                    sx={{
                                                        color:
                                                            isCardInUse && hasActiveSubscription
                                                                ? '#9CA3AF'
                                                                : '#374151',
                                                        fontSize: '12px',
                                                        minWidth: '120px',
                                                        px: 2,
                                                        '&:hover': {
                                                            backgroundColor:
                                                                isCardInUse && hasActiveSubscription
                                                                    ? 'transparent'
                                                                    : '#F9FAFB',
                                                        },
                                                        '&.Mui-disabled': {
                                                            opacity: 0.6,
                                                        },
                                                    }}
                                                >
                                                    삭제
                                                </MenuItem>
                                            </>
                                        );
                                    })()}
                                </Menu>

                                {/* Add Payment Method Button */}
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        py: 2,
                                    }}
                                >
                                    <Add sx={{ fontSize: '20px', color: '#9CA3AF' }} />
                                    <Typography
                                        sx={{
                                            fontSize: '14px',
                                            color: '#9CA3AF',
                                            cursor: cardLoading ? 'not-allowed' : 'pointer',
                                            opacity: cardLoading ? 0.6 : 1,
                                            '&:hover': {
                                                color: cardLoading ? '#9CA3AF' : '#6B7280',
                                            },
                                        }}
                                        onClick={() => {
                                            if (!cardLoading) {
                                                handleAddPaymentMethod();
                                            }
                                        }}
                                    >
                                        {cardLoading ? '연결 중...' : '결제 방식 추가'}
                                    </Typography>
                                </Box>
                            </Box>

                            <Divider sx={{ my: 3, mb: 6 }} />

                            {/* Billing History Section */}
                            <Box>
                                <Typography
                                    sx={{
                                        fontSize: '18px',
                                        fontWeight: 600,
                                        color: '#1F2937',
                                        mb: 2,
                                    }}
                                >
                                    청구서 내역
                                </Typography>

                                {/* Billing History List */}
                                <Box sx={{ mb: 5 }}>
                                    {orderLoading ? (
                                        <Box sx={{ py: 4, textAlign: 'center' }}>
                                            <Typography sx={{ color: '#6B7280' }}>
                                                청구서 내역을 불러오는 중...
                                            </Typography>
                                        </Box>
                                    ) : orderData?.length > 0 ? (
                                        orderData?.map((order) => (
                                            <Box
                                                key={order.id}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    py: 2,
                                                    borderBottom: '1px solid #F3F4F6',
                                                }}
                                            >
                                                <Box
                                                    sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flex: 1 }}
                                                >
                                                    <Box sx={{ flex: 1 }}>
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'space-between',
                                                                mb: 0.5,
                                                            }}
                                                        >
                                                            <Typography sx={{ fontSize: '14px', color: '#6B7280' }}>
                                                                {new Date(order.createdAt).toLocaleDateString('ko-KR', {
                                                                    year: 'numeric',
                                                                    month: 'numeric',
                                                                    day: 'numeric',
                                                                })}
                                                            </Typography>
                                                        </Box>
                                                        <Typography
                                                            sx={{ fontSize: '16px', color: '#374151', fontWeight: 500 }}
                                                        >
                                                            {order.productName} - {order.product.creditAmount} 크레딧
                                                        </Typography>
                                                        <Typography
                                                            sx={{
                                                                fontSize: '12px',
                                                                color: '#9CA3AF',
                                                                mt: 0.5,
                                                                whiteSpace: 'nowrap',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                            }}
                                                        >
                                                            주문번호: {order.orderNumber}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'flex-end',
                                                        gap: 1,
                                                        alignSelf: isCancellable(order) ? 'flex-start' : 'center',
                                                        mt: isCancellable(order) ? 0 : 0,
                                                    }}
                                                >
                                                    {isCancellable(order) && (
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            onClick={() => handleCancelOrder(order)}
                                                            disabled={cancelingOrderId === order.id}
                                                            sx={{
                                                                color: '#DC2626',
                                                                borderColor: '#DC2626',
                                                                fontSize: '12px',
                                                                fontWeight: 500,
                                                                px: 2,
                                                                py: 1,
                                                                borderRadius: 1.5,
                                                                textTransform: 'none',
                                                                minWidth: '60px',
                                                                height: '28px',
                                                                flexShrink: 0,
                                                                '&:hover': {
                                                                    backgroundColor: '#FEE2E2',
                                                                    borderColor: '#B91C1C',
                                                                },
                                                                '&:disabled': {
                                                                    color: '#9CA3AF',
                                                                    borderColor: '#D1D5DB',
                                                                },
                                                            }}
                                                        >
                                                            {cancelingOrderId === order.id ? '취소 중...' : '취소'}
                                                        </Button>
                                                    )}
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                        <Typography
                                                            sx={{ fontSize: '16px', color: '#374151', fontWeight: 600 }}
                                                        >
                                                            {order.orderStatusCode === 80000 &&
                                                            order.fittingOrderPayment?.refundedAmount
                                                                ? `₩${order.fittingOrderPayment.refundedAmount.toLocaleString()}`
                                                                : `₩${order.fittingOrderPayment?.paidAmount.toLocaleString()}`}
                                                        </Typography>
                                                        <Chip
                                                            label={order.orderStatus}
                                                            size="small"
                                                            sx={{
                                                                backgroundColor:
                                                                    order.orderStatusCode === 20000
                                                                        ? '#D1FAE5'
                                                                        : '#FEE2E2',
                                                                color:
                                                                    order.orderStatusCode === 20000
                                                                        ? '#065F46'
                                                                        : '#991B1B',
                                                                fontSize: '12px',
                                                                fontWeight: 500,
                                                            }}
                                                        />
                                                    </Box>
                                                </Box>
                                            </Box>
                                        ))
                                    ) : (
                                        <Box sx={{ py: 4, textAlign: 'center' }}>
                                            <Typography sx={{ color: '#6B7280' }}>청구서 내역이 없습니다.</Typography>
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        </Box>
                    </TabPanel>
                </Paper>
            </Container>
            <Footer />

            {/* 구독 취소 확인 다이얼로그 */}
            <SubscriptionCancelDialog
                open={cancelDialogOpen}
                onClose={() => setCancelDialogOpen(false)}
                onConfirm={handleConfirmRefund}
                subscriptionData={subscriptionData || undefined}
            />

            {/* 크레딧 구매 다이얼로그 */}
            <CreditPurchaseDialog
                open={creditPurchaseDialogOpen}
                onClose={() => setCreditPurchaseDialogOpen(false)}
                onPurchase={handleCreditPurchase}
                userId={vendorId || undefined}
            />

            {/* 확인 다이얼로그 */}
            <ConfirmDialog />
        </HomeContainer>
    );
}
